import type { JSONFieldValues } from "@adrekic/fieldsjson"
import {
	type Field,
	FieldSchema,
	passFailFieldStatusMapping,
	type SerializedValueOfField,
	UploadField,
} from "@overmap-ai/forms"

import { getFieldCsvColumnHeader } from "../../utils"
import type { CSVColumnDef } from "../typings"

export const getCSVColumnDefOfField = <TData>(
	field: Exclude<Field, UploadField>,
	getValues: (data: TData) => JSONFieldValues | undefined,
): CSVColumnDef<TData> | CSVColumnDef<TData>[] => {
	switch (field.type) {
		case "boolean":
			return {
				id: field.id,
				header: getFieldCsvColumnHeader(field),
				accessorFn: (data) => {
					const values = getValues(data)
					if (!values) return ""
					const value = values[field.id] as SerializedValueOfField<typeof field> | undefined
					if (value === undefined || field.isSerializedValueBlank(value)) return ""
					return value
				},
			}
		case "checkbox-list":
		case "multi-select":
		case "multi-string":
			return {
				id: field.id,
				header: getFieldCsvColumnHeader(field),
				accessorFn: (data) => {
					const values = getValues(data)
					if (!values) return ""
					const value = values[field.id] as SerializedValueOfField<typeof field> | undefined
					if (value === undefined || field.isSerializedValueBlank(value)) return ""
					return value.join(", ")
				},
			}
		case "pass-fail":
			return [
				{
					id: field.id,
					header: getFieldCsvColumnHeader(field),
					accessorFn: (data) => {
						const values = getValues(data)
						if (!values) return ""
						const value = values[field.id] as SerializedValueOfField<typeof field> | undefined
						if (value === undefined || field.isSerializedValueBlank(value)) return ""
						return passFailFieldStatusMapping[value.status!].label
					},
				},
				{
					id: `${field.id}-notes`,
					header: `${getFieldCsvColumnHeader(field)} (notes)`,
					accessorFn: (data) => {
						const values = getValues(data)
						if (!values) return ""
						const value = values[field.id] as SerializedValueOfField<typeof field> | undefined
						if (value === undefined || field.isSerializedValueBlank(value)) return ""
						return value.notes
					},
				},
			]
		case "rating":
		case "number":
			return {
				id: field.id,
				header: getFieldCsvColumnHeader(field),
				accessorFn: (data) => {
					const values = getValues(data)
					if (!values) return ""
					const value = values[field.id] as SerializedValueOfField<typeof field> | undefined
					if (value === undefined || field.isSerializedValueBlank(value)) return ""
					return value
				},
			}
		case "date":
		case "radio":
		case "select":
		case "url":
			return {
				id: field.id,
				header: getFieldCsvColumnHeader(field),
				accessorFn: (data) => {
					const values = getValues(data)
					if (!values) return ""
					const value = values[field.id] as SerializedValueOfField<typeof field> | undefined
					if (value === undefined || field.isSerializedValueBlank(value)) return ""
					return value
				},
			}
		case "time":
			return {
				id: field.id,
				header: getFieldCsvColumnHeader(field),
				accessorFn: (data) => {
					const values = getValues(data)
					if (!values) return ""
					const value = values[field.id] as SerializedValueOfField<typeof field> | undefined
					if (value === undefined || field.isSerializedValueBlank(value)) return ""
					return value
				},
			}
		case "qr":
		case "string":
		case "text":
		case "otp":
			return {
				id: field.id,
				header: getFieldCsvColumnHeader(field),
				accessorFn: (data) => {
					const values = getValues(data)
					if (!values) return ""
					const value = values[field.id] as SerializedValueOfField<typeof field> | undefined
					if (value === undefined || field.isSerializedValueBlank(value)) return ""
					return value
				},
			}
	}
}

export const getCSVColumnDefsOfFields = <TData>(
	fields: Field[],
	getValues: (data: TData) => JSONFieldValues | undefined,
): CSVColumnDef<TData>[] => {
	const supportedFields: Exclude<Field, UploadField>[] = fields.filter((field) => field.type !== "upload")
	return supportedFields.flatMap((field) => getCSVColumnDefOfField(field, getValues))
}

export const getCSVColumnDefsOfFieldSchema = <TData>(
	fieldSchema: FieldSchema,
	getValues: (data: TData) => JSONFieldValues | undefined,
): CSVColumnDef<TData>[] => {
	return getCSVColumnDefsOfFields(fieldSchema.getFields(), getValues)
}
